import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ExternalLink, AlertCircle, Loader2, Trash2, RefreshCw } from 'lucide-react';
import Sidebar, { SidebarProvider } from '../../components/navigation/Sidebar';
import { wordpressService } from '../../services/wordpressService';
import { format } from 'date-fns';
import { supabase } from '../../lib/supabase';
import { getAuthSessionHeaders } from '../../services/sessionService';
import { useModal } from '../../components/ui/ModalProvider';
import { useLanguageProfile } from '../../contexts/LanguageProfileContext';
import { useRequestGuard } from '../../hooks/useRequestGuard';




export default function BlogPosts() {
  const navigate = useNavigate();
  const { confirm } = useModal();
  const { activeProfile } = useLanguageProfile();
  const startSitesRequest = useRequestGuard();
  const startArticlesRequest = useRequestGuard();

  const [selectedSite, setSelectedSite] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sites, setSites] = useState([]);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [deletingIds, setDeletingIds] = useState(new Set());
  
  // 📄 PAGINATION STATE
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const ITEMS_PER_PAGE = 20;

// The staged article-generation queue (queued -> generating -> content_ready
// -> publishing -> completed, with generation_error/publishing_error on
// failure) replaced the single old 'processing' status with several
// stage-specific ones. This page still only needs to show two collapsed
// states to the user — "processing" while any of them is in flight, "error"
// for either failure stage — so IN_FLIGHT_STATUSES/ERROR_STATUSES map the
// new granular statuses back onto the badges/behavior this page already had,
// without this display layer needing to know about queue internals.
const IN_FLIGHT_STATUSES = ['processing', 'generating', 'content_ready', 'publishing'];
const ERROR_STATUSES = ['error', 'generation_error', 'publishing_error'];
const VISIBLE_STATUSES = ['queued', ...IN_FLIGHT_STATUSES, 'completed', ...ERROR_STATUSES, 'cancelled'];

  const StatusBadge = ({ status }) => {
    const displayStatus = IN_FLIGHT_STATUSES?.includes(status)
      ? 'processing'
      : ERROR_STATUSES?.includes(status)
      ? 'error'
      : status;

    const map = {
      queued: 'bg-blue-100 text-blue-800',
      processing: 'bg-yellow-100 text-yellow-800 animate-pulse',
      completed: 'bg-green-100 text-green-800',
      error: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-200 text-gray-800',
    };

    if (!map?.[displayStatus]) return null;

    return (
      <span className={`px-3 py-1 text-xs font-semibold rounded-full ${map?.[displayStatus]}`}>
        {displayStatus}
      </span>
    );
  };

  // ✅ Load sites
  useEffect(() => {
    const isCurrent = startSitesRequest();
    wordpressService?.getAllSites(activeProfile?.id)
      ?.then((data) => { if (isCurrent()) setSites(data); })
      ?.catch(() => { if (isCurrent()) setError('Failed to load sites'); });
  }, [activeProfile?.id, startSitesRequest]);

  // Clear the previous profile's articles immediately on switch so stale
  // content can't flash while the new profile's data is loading.
  useEffect(() => {
    setArticles([]);
    setTotalCount(0);
  }, [activeProfile?.id]);

  // ✅ Fetch articles with pagination
  const fetchArticles = useCallback(async () => {
    const isCurrent = startArticlesRequest();
    setLoading(true);
    setError(null);

    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) {
  setLoading(false);
  return;
}


      // Build query
      let query = supabase?.from('wp_articles')?.select(`
          id,
          site_id,
          title,
          image_main_url,
          status,
          focus_keyword,
          wp_post_url,
          error_message,
          created_at,
          updated_at,
          wordpress_category_id,
          wp_sites!inner(site_name, user_id)
        `, { count: 'exact' })?.eq('wp_sites.user_id', user?.id);

      if (activeProfile?.id) {
        query = query?.eq('profile_id', activeProfile.id);
      }

      // Apply filters
      if (selectedSite !== 'all') {
        query = query?.eq('site_id', selectedSite);
      }

      if (selectedStatus === 'processing') {
        query = query?.in('status', IN_FLIGHT_STATUSES);
      } else if (selectedStatus === 'error') {
        query = query?.in('status', ERROR_STATUSES);
      } else if (selectedStatus !== 'all') {
        query = query?.eq('status', selectedStatus);
      }

      // Apply search
      if (searchQuery?.trim()) {
        query = query?.ilike('title', `%${searchQuery}%`);
      }

      // Apply pagination
      const from = (currentPage - 1) * ITEMS_PER_PAGE;
      const to = from + ITEMS_PER_PAGE - 1;

      query = query?.order('created_at', { ascending: false })?.range(from, to);

      const { data, error: fetchError, count } = await query;
      if (fetchError) throw fetchError;

      const filtered = (data || [])?.filter(a =>
        VISIBLE_STATUSES?.includes(a?.status) || selectedStatus === 'all'
      );

      if (!isCurrent()) return;
      setArticles(filtered);
      setTotalCount(count || 0);

    } catch (e) {
      if (!isCurrent()) return;
      setError('Failed to load articles');
      console.error(e);
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [selectedSite, selectedStatus, searchQuery, currentPage, activeProfile?.id, startArticlesRequest]);

  useEffect(() => { 
    fetchArticles(); 
  }, [fetchArticles]);

  // ✅ Auto-refresh every 15s
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(fetchArticles, 15000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchArticles]);

  

  // 🔄 RETRY: Set error articles back to queued
  const handleRetry = async (articleId) => {
    try {
      await supabase?.from('wp_articles')?.update({
          status: 'queued',
          error_message: null,
          updated_at: new Date()?.toISOString()
        })?.eq('id', articleId);

      await fetchArticles();
    } catch (err) {
      console.error('Retry error:', err);
      setError('Failed to retry article');
    }
  };




// ⛔ CANCEL: Stop processing article
const handleCancel = async (articleId) => {
  if (!(await confirm('Cancel this article?'))) return;

  try {
    const { data, error } = await supabase
      .from('wp_articles')
      .update({
        status: 'cancelled',
        error_message: 'Cancelled by user',
        updated_at: new Date().toISOString(),
      })
      .eq('id', articleId)
      .in('status', IN_FLIGHT_STATUSES)
      .select('id,status');

    if (error || !data || data.length === 0) {
      setError('Failed to cancel article');
      return;
    }

    // 🔴 THESE TWO LINES ARE THE FIX
    await fetchArticles();
    await supabase.functions.invoke('process-article-queue', {
      headers: await getAuthSessionHeaders()
    });

  } catch (err) {
    console.error(err);
    setError('Failed to cancel article');
  }
};





  // 🗑️ DELETE: Remove article from database
  const handleDelete = async (articleId, isProcessing) => {
    if (isProcessing) return; // Prevent deletion during processing

    if (!(await confirm('Are you sure you want to delete this article?'))) {
      return;
    }

    setDeletingIds(prev => new Set([...prev, articleId]));
    setError(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();

      const { error: deleteError, count } = await supabase
        .from('wp_articles')
        .delete({ count: 'exact' })
        .eq('id', articleId)
        .eq('user_id', user.id);

      if (deleteError) throw deleteError;
      if (count === 0) throw new Error('Article not found or you do not have permission to delete it.');

      setArticles(prev => prev.filter(a => a.id !== articleId));
    } catch (err) {
      console.error('Delete error:', err);
      setError(err.message || 'Failed to delete article');
    } finally {
      setDeletingIds(prev => {
        const newSet = new Set(prev);
        newSet?.delete(articleId);
        return newSet;
      });
    }
  };

  // 📄 PAGINATION
  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  return (
    <SidebarProvider>
      <div className="min-h-screen bg-gray-50">
        <Sidebar />
        <main className="main-content">
          <div className="max-w-7xl mx-auto">

            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-6 sm:mb-8">
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Blog Posts Processing</h1>
                <p className="text-sm text-gray-500 mt-1">
                  Queue-based WordPress article generation
                </p>
              </div>
              <div className="flex gap-3">
                <label className="flex items-center gap-2 text-sm text-gray-600">
                  <input
                    type="checkbox"
                    checked={autoRefresh}
                    onChange={(e) => setAutoRefresh(e?.target?.checked)}
                  />
                  Auto refresh (15s)
                </label>
              </div>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-xl border shadow-sm p-4 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

                {/* Website */}
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1">
                    Website
                  </label>
                  <select
                    value={selectedSite}
                    onChange={(e) => {
                      setSelectedSite(e?.target?.value);
                      setCurrentPage(1);
                    }}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                  >
                    <option value="all">All Websites</option>
                    {sites?.map(site => (
                      <option key={site?.id} value={site?.id}>
                        {site?.site_name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Status */}
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1">
                    Status
                  </label>
                  <select
                    value={selectedStatus}
                    onChange={(e) => {
                      setSelectedStatus(e?.target?.value);
                      setCurrentPage(1);
                    }}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                  >
                    <option value="all">All Statuses</option>
                    <option value="queued">Queued</option>
                    <option value="processing">Processing</option>
                    <option value="completed">Completed</option>
                    <option value="error">Error</option>
                  </select>
                </div>

                {/* Search */}
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1">
                    Search
                  </label>
                  <input
                    type="text"
                    placeholder="Search by title..."
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e?.target?.value);
                      setCurrentPage(1);
                    }}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                  />
                </div>

              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 p-4 rounded-lg mb-6 flex gap-3">
                <AlertCircle className="text-red-600" />
                <div>
                  <p className="font-semibold text-red-800">Error</p>
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              </div>
            )}

            {/* Articles Table */}
            {!loading && !error && (
              <>
                <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
                  <div className="overflow-x-auto">
                  <table className="w-full min-w-[640px]">
                    <thead className="bg-gray-100 text-xs uppercase text-gray-500">
                      <tr>
                        <th className="px-6 py-3 text-left">Article</th>
                        <th className="px-6 py-3">Status</th>
                        <th className="px-6 py-3">WordPress URL</th>
                        <th className="px-6 py-3">Created</th>
                        <th className="px-6 py-3">Actions</th>
                      </tr>
                    </thead>

                    <tbody className="divide-y">
                      {articles?.map(article => (
                        <tr key={article?.id} className="hover:bg-gray-50">
                        
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              {article?.image_main_url && (
                                <img
                                  src={article?.image_main_url}
                                  alt={article?.title}
                                  className="w-16 h-16 object-cover rounded border"
                                />
                              )}
                              <div>
                                <p className="font-semibold text-gray-900">
                                  {article?.title}
                                </p>
                                <p className="text-xs text-gray-500">{article?.wp_sites?.site_name}</p>
                                {article?.error_message && (
                                  <p className="text-xs text-red-600 mt-1">{article?.error_message}</p>
                                )}
                              </div>
                            </div>
                          </td>

                          <td className="px-6 py-4 text-center">
                            <StatusBadge status={article?.status} />
                          </td>

                          <td className="px-6 py-4 text-sm text-center">
                            {article?.wp_post_url ? (
                              <a
                                href={article?.wp_post_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline inline-flex items-center gap-1"
                              >
                                Open
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>

                          <td className="px-6 py-4 text-sm text-gray-500 text-center">
                            {format(new Date(article.created_at), 'dd MMM yyyy HH:mm')}
                          </td>

                        
<td className="px-6 py-4 text-center">
  <div className="flex items-center justify-center gap-2">

    {IN_FLIGHT_STATUSES?.includes(article?.status) && (
      <button
        onClick={() => handleCancel(article?.id)}
        className="p-2 text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
        title="Cancel"
      >
        ✖
      </button>
    )}

    {/* Retry button for error or cancelled status */}
    {(ERROR_STATUSES?.includes(article?.status) || article?.status === 'cancelled') && (
      <button
        onClick={() => handleRetry(article?.id)}
        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
        title="Retry"
      >
        <RefreshCw className="w-4 h-4" />
      </button>
    )}

    {/* Delete button (disabled during processing) */}
    <button
      onClick={() => handleDelete(article?.id, IN_FLIGHT_STATUSES?.includes(article?.status))}
      disabled={IN_FLIGHT_STATUSES?.includes(article?.status) || deletingIds?.has(article?.id)}
      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
      title={IN_FLIGHT_STATUSES?.includes(article?.status) ? 'Cannot delete while processing' : 'Delete'}
    >
      {deletingIds?.has(article?.id) ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : (
        <Trash2 className="w-4 h-4" />
      )}
    </button>

  </div>
</td>





                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>

                  {articles?.length === 0 && (
                    <div className="py-12 text-center text-gray-500">
                      No articles found
                    </div>
                  )}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
                    <div className="text-sm text-gray-600">
                      Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1} to {Math.min(currentPage * ITEMS_PER_PAGE, totalCount)} of {totalCount} articles
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="px-4 py-2 border rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>

                      {[...Array(totalPages)]?.map((_, index) => {
                        const page = index + 1;
                        // Show first, last, current, and pages around current
                        if (
                          page === 1 ||
                          page === totalPages ||
                          (page >= currentPage - 1 && page <= currentPage + 1)
                        ) {
                          return (
                            <button
                              key={page}
                              onClick={() => handlePageChange(page)}
                              className={`px-4 py-2 border rounded-lg text-sm font-medium ${
                                currentPage === page
                                  ? 'bg-blue-600 text-white border-blue-600' :'hover:bg-gray-50'
                              }`}
                            >
                              {page}
                            </button>
                          );
                        } else if (
                          page === currentPage - 2 ||
                          page === currentPage + 2
                        ) {
                          return <span key={page} className="px-2">...</span>;
                        }
                        return null;
                      })}

                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="px-4 py-2 border rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}

            {loading && (
              <div className="flex justify-center items-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              </div>
            )}

          </div>
        </main>
      </div>
    </SidebarProvider>
  );
}
